import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { handleResult } from 'modules/core/lib/handleMutation'

import type {
  ClipboardHandlers,
  ClipboardPayloadEntry,
} from 'modules/shift_management/components/ShiftManagement/TimeGridTable/types'
import type { ShiftsRow } from 'modules/shift_management/lib/types'
import type {
  GridClipboardPayload,
  ExtendedGridEventAppearance,
} from 'modules/shift_management/utils/TimeGridTable/transformEvents'
import {
  useBulkDropSchedule,
  type SchedulePasteItem,
} from 'modules/shift_management/hooks/gridTable/useBulkDropSchedule'
import { i18nDateToString } from 'modules/core/lib/dates'
import { useShiftsRows } from 'modules/shift_management/hooks/useShiftsRows'
import { useRefreshStats } from 'modules/shift_management/hooks/refreshShiftsRows'

interface UseClipboardProps {
  canUseClipboardActions: boolean
  shiftsRows: ShiftsRow[]
}

type ShiftPayload = Extract<GridClipboardPayload, { kind: 'shift' }>
type RestDayPayload = Extract<GridClipboardPayload, { kind: 'rest_day' }>

const isShiftPayload = (payload: GridClipboardPayload): payload is ShiftPayload =>
  payload.kind === 'shift'

const isRestDayPayload = (payload: GridClipboardPayload): payload is RestDayPayload =>
  payload.kind === 'rest_day'

const payloadIdentityKey = (payload: GridClipboardPayload): string => {
  if (payload.kind === 'shift') return `shift:${payload.shift.id}`

  return `rest_day:${payload.restDay.id}`
}

const useGridClipboardActions = ({
  canUseClipboardActions,
  shiftsRows,
}: UseClipboardProps):
  | ClipboardHandlers<{ id: string }, ExtendedGridEventAppearance>
  | undefined => {
  const [{ shiftsRows: currentShiftsRows }] = useShiftsRows()
  const { refreshStats } = useRefreshStats()
  const bulkDropSchedule = useBulkDropSchedule()

  return useUnsafeMemo(() => {
    if (!canUseClipboardActions) return undefined

    const shiftsRowsByKey = new Map(shiftsRows.map((row) => [row.key, row]))

    const extractSourceData = (payload: GridClipboardPayload[]) => {
      const sourceShifts = payload.filter(isShiftPayload).map((item) => item.shift)
      const sourceRestDays = payload.filter(isRestDayPayload).map((item) => item.restDay)

      return {
        sourceShifts,
        sourceShiftIds: new Set(sourceShifts.map((shift) => shift.id)),
        sourceRestDays,
        sourceRestDayIds: new Set(sourceRestDays.map((restDay) => restDay.id)),
      }
    }

    const hasTargetItemsToReplace = ({
      targetPayload,
      sourceShiftIds,
      sourceRestDayIds,
    }: {
      targetPayload: GridClipboardPayload[]
      sourceShiftIds: Set<string>
      sourceRestDayIds: Set<string>
    }) =>
      targetPayload.some((payload) => {
        if (isShiftPayload(payload)) return !sourceShiftIds.has(payload.shift.id)

        return !sourceRestDayIds.has(payload.restDay.id)
      })

    const pasteScheduleBatch = async ({ items }: { items: SchedulePasteItem[] }) => {
      const validItems = items.filter(
        ({ sourceShifts, sourceRestDays, removeTargetItems }) =>
          sourceShifts.length > 0 || sourceRestDays.length > 0 || removeTargetItems
      )
      if (validItems.length === 0) return true

      const result = await bulkDropSchedule({
        variables: {
          items: validItems.map(
            ({
              sourceShifts,
              sourceRestDays,
              targetRowId,
              targetDate,
              mode,
              removeTargetItems,
              dropOverrides,
            }) => ({
              shiftIds: sourceShifts.map((shift) => shift.id),
              restDayIds: sourceRestDays.map((restDay) => restDay.id),
              employeeId: targetRowId,
              removeTargetItems,
              date: targetDate,
              dropAction: mode,
              startTime: dropOverrides?.startTime ?? null,
              locationId: dropOverrides?.locationId ? String(dropOverrides.locationId) : null,
              workAreaId: dropOverrides?.workAreaId ? String(dropOverrides.workAreaId) : null,
            })
          ),
        },
      })

      const handled = handleResult(result, (_errors) => {})
      if (!handled.success) return false

      return true
    }

    return {
      getCopyPayload: (context) => {
        const seenPayloads = new Set<string>()
        const payloadEntries = context.cells.map<ClipboardPayloadEntry>((selectionCell) => {
          const payload = selectionCell.context.events
            .flatMap((event) => (event.clipboardPayload ? [event.clipboardPayload] : []))
            .filter((clipboardPayload) => {
              const identity = payloadIdentityKey(clipboardPayload)
              if (seenPayloads.has(identity)) return false

              seenPayloads.add(identity)
              return true
            })

          return {
            rowOffset: selectionCell.rowOffset,
            columnOffset: selectionCell.columnOffset,
            payload,
          }
        })

        if (payloadEntries.every((entry) => entry.payload.length === 0)) return null

        return payloadEntries
      },
      canPaste: () => true,
      onPaste: async (context) => {
        const shouldReplaceTargetContent = context.replaceTargetContent !== false
        const targetCellsByOffset = new Map(
          context.target.cells.map((selectionCell) => [
            `${selectionCell.rowOffset}:${selectionCell.columnOffset}`,
            selectionCell.context,
          ])
        )

        const payloadByTargetCell = new Map<
          string,
          {
            targetCell: (typeof context.target.cells)[number]['context']
            payload: GridClipboardPayload[]
          }
        >()

        context.payload.forEach((payloadEntry) => {
          const key = `${payloadEntry.rowOffset}:${payloadEntry.columnOffset}`
          const targetCell = targetCellsByOffset.get(key)
          if (!targetCell) return

          const current = payloadByTargetCell.get(key)
          if (!current) {
            payloadByTargetCell.set(key, { targetCell, payload: payloadEntry.payload })
            return
          }

          current.payload = [...current.payload, ...payloadEntry.payload]
        })

        const schedulePasteItems: SchedulePasteItem[] = []

        for (const { targetCell, payload } of payloadByTargetCell.values()) {
          const targetRow = shiftsRowsByKey.get(targetCell.entity.id)
          if (!targetRow) continue

          const { sourceShifts, sourceShiftIds, sourceRestDays, sourceRestDayIds } =
            extractSourceData(payload)

          const targetPayload = targetCell.events.flatMap((event) =>
            event.clipboardPayload ? [event.clipboardPayload] : []
          )

          const shouldRemoveTargetItems = hasTargetItemsToReplace({
            targetPayload,
            sourceShiftIds,
            sourceRestDayIds,
          })

          const targetDate = i18nDateToString(targetCell.slot.start, 'backend')
          const targetKey = `${targetCell.entity.id}:${targetCell.slot.index}`
          const dropOverrides = context.dropOverridesByOffset?.[targetKey]

          if (
            sourceShifts.length > 0 ||
            sourceRestDays.length > 0 ||
            (shouldReplaceTargetContent && shouldRemoveTargetItems)
          ) {
            schedulePasteItems.push({
              sourceShifts,
              sourceRestDays,
              targetRowId: targetRow.id,
              targetDate,
              mode: context.mode,
              removeTargetItems: shouldReplaceTargetContent,
              dropOverrides,
            })
          }
        }

        const schedulePasted = await pasteScheduleBatch({
          items: schedulePasteItems,
        })
        if (!schedulePasted) return

        if (payloadByTargetCell.size === 0) return

        refreshStats(currentShiftsRows)
      },
    }
  }, [canUseClipboardActions, bulkDropSchedule, shiftsRows, currentShiftsRows, refreshStats])
}

export { useGridClipboardActions }
